import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";

import { DocumentFilters, QuotationModel } from "api";
import { useDocumentService } from "contexts/ServiceContext";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { getProductName } from "util/getProductName";
import { formatQuantityValue, valueOr, valueToDate } from "util/statHelpers";
import { AI_EVENTS, useTrackEvent } from "../../useAppInsight";
import { QuotationModelWithOrderStatus } from "services/ContractService";

const mapApiDocumentContractValues = (
  contract: QuotationModelWithOrderStatus
): DocumentContract => ({
  docEntry: contract.DocEntry,
  contractNumber: `${valueOr(contract.ContractNumber)}`,
  poNumber: valueOr(contract.NumAtCard),
  contractDate: valueToDate(contract.ContractDate),
  productName: getProductName(contract.DocumentLines),
  status: valueOr(contract.ContractStatus),
  shipStartDate: valueToDate(contract.ShipDateStart),
  shipEndDate: valueToDate(contract.ShipDateEnd),
  quantityTotalFormatted: formatQuantityValue(contract.CalculatedSumQuantity), // Ideally we should be consistent and use ContractQuantityCommitted, but that is only available if $expand contains "ContractTotals"
  quantityRemainingFormatted: formatQuantityValue(
    contract.ContractQuantityRemainingToOrder
  ),
  unitOfMeasure: contract.DocumentLines?.[0]?.UnitOfMeasureCode ?? "",
});

const mapApiDocumentContractFilters = (
  filters?: DocumentsContractsFilters
): DocumentFilters => {
  return {
    CardCodes: filters?.cardCodes,
    ContractDateEnd: filters?.contractDateEndDate,
    ContractDateStart: filters?.contractDateStartDate,
    EndShipDateEnd: filters?.endShipEndDate,
    EndShipDateStart: filters?.endShipStartDate,
    ProductCodes: filters?.product,
    SearchString: filters?.searchString,
    StartShipDateEnd: filters?.startShipEndDate,
    StartShipDateStart: filters?.startShipStartDate,
    StatusCodes: filters?.status,
  };
};

const useDocumentContracts = (
  currentSort?: { key: keyof QuotationModel; direction: string },
  requestBody?: DocumentsContractsFilters
): UseInfiniteQueryResult<{ contracts: DocumentContract[] }> => {
  const cardCodes = useSelectedCustomerCode();
  const documentService = useDocumentService();
  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    cardCodes,
  });
  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    cardCodes,
  });
  return useInfiniteQuery({
    queryKey: ["documentContracts", { currentSort, ...requestBody }],
    queryFn: ({ pageParam = 0 }) => {
      const filterParams = {
        sort: currentSort,
        page: pageParam,
      };
      const eventId = trackQuery({
        endpoint: "Quotation/SearchNFilterGridView",
        parameters: filterParams,
      });
      return documentService
        .getDocumentContractList(
          filterParams,
          mapApiDocumentContractFilters(requestBody)
        )
        .then(({ value, ...rest }) => ({
          ...rest,
          contracts: value?.map(mapApiDocumentContractValues) ?? [],
        }))
        .catch((err) => {
          trackQueryError(
            {
              statusCode: err.statusCode,
              message: err.message,
            },
            eventId
          );
          throw err;
        });
    },
  });
};

export default useDocumentContracts;
